import { TenantAuthorizationAction } from "@/features/auth/tenant-authorization/model/tenant-authorization.types";

export type TenantRouteAuthorizationPolicy =
  | { kind: "PUBLIC_SESSION" }
  | { kind: "ACTION"; action: TenantAuthorizationAction }
  | { kind: "DENY_PENDING_PRODUCT_POLICY" };

/**
 * Branch Details names a target branch ID. Its own layout resolves the exact owner-or-branch-assignment
 * decision before rendering. It must not inherit the parish `/branches` list policy, which has no target ID.
 */
export function isBranchDetailsCanonicalPath(pathname: string): boolean {
  return /^\/branches\/[^/]+(?:\/|$)/.test(pathname);
}

const PUBLIC_SESSION_PATHS = new Set([
  "/login", "/register", "/auth/failure", "/accept-invitation", "/create-church", "/register-church",
  "/account-blocked", "/auth/callback", "/no-access", "/not-found", "/workspaces", "/workspace-selector",
]);

const ACTION_PATH_PREFIXES: ReadonlyArray<readonly [string, TenantAuthorizationAction]> = [
  // More-specific creation routes must precede their read-only module family. A visible create button and a
  // pasted URL must ask the same exact action.
  ["/members/create", TenantAuthorizationAction.MembersEdit],
  ["/members/import", TenantAuthorizationAction.MembersEdit],
  ["/activities/create", TenantAuthorizationAction.ActivitiesEdit],
  ["/assets/items/new", TenantAuthorizationAction.AssetsEdit],
  ["/forms/create", TenantAuthorizationAction.FormsEdit],
  ["/finance/transactions/create", TenantAuthorizationAction.TransactionCreate],
  ["/finance/transfers/requests/create", TenantAuthorizationAction.TransactionCreate],
  ["/finance/transfers/create", TenantAuthorizationAction.TransactionCreate],
  ["/finance/pledges/create", TenantAuthorizationAction.TransactionCreate],
  ["/ministers/create", TenantAuthorizationAction.MinistriesEdit],
  // Organization Settings is a tabbed surface. A user who can manage only the team must not be blocked by
  // the generic SETTINGS family, nor routed through the Organization tab first.
  ["/settings/team", TenantAuthorizationAction.UsersView],
  ["/settings/invitations", TenantAuthorizationAction.UsersView],
  ["/settings/roles", TenantAuthorizationAction.RolesManage],
  ["/settings/subscription", TenantAuthorizationAction.SubscriptionView],
  ["/settings/organization", TenantAuthorizationAction.SettingsView],
  ["/members/groups", TenantAuthorizationAction.GroupsView],
  ["/members/ministries", TenantAuthorizationAction.MinistriesView],
  ["/members", TenantAuthorizationAction.MembersView],
  ["/activities", TenantAuthorizationAction.ActivitiesView],
  ["/departments", TenantAuthorizationAction.DepartmentsView],
  ["/assets", TenantAuthorizationAction.AssetsView],
  ["/forms", TenantAuthorizationAction.FormsView],
  ["/finance", TenantAuthorizationAction.FinanceView],
  ["/global-finance", TenantAuthorizationAction.FinanceView],
  // Paused routes still require budgeting access. “Coming soon” is a page state, not an authorization bypass.
  ["/budgets", TenantAuthorizationAction.BudgetingView],
  ["/ministries", TenantAuthorizationAction.MinistriesView],
  ["/reports", TenantAuthorizationAction.ReportsView],
  ["/audit-log", TenantAuthorizationAction.AuditLogView],
  ["/settings", TenantAuthorizationAction.SettingsView],
  ["/branches", TenantAuthorizationAction.BranchesManage],
] as const;

/**
 * Fail-closed route policy used while the full route inventory is migrated. Every path receives an explicit
 * disposition: public/session, a typed authorization action, or a visible product-policy denial.
 */
export function resolveTenantRouteAuthorizationPolicy(pathname: string): TenantRouteAuthorizationPolicy {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (PUBLIC_SESSION_PATHS.has(path) || path === "/" || path === "/dashboard") return { kind: "PUBLIC_SESSION" };
  if (/\/assets\/items\/[^/]+\/edit(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.AssetsEdit };
  }
  if (/\/forms\/[^/]+\/edit(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.FormsEdit };
  }
  if (/\/forms\/[^/]+\/settings(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.FormsEdit };
  }
  if (/\/finance\/transfers\/requests\/[^/]+\/edit(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.TransactionCreate };
  }
  if (/\/reports\/[^/]+\/configure(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.ReportsEdit };
  }
  /*
   * Department access and settings are management surfaces. ADR-0027: "Only a Department Manager, or broader
   * branch authority accepted by centralized authorization, may assign department access or view settings."
   * Without these the generic `/departments` prefix resolved them to DepartmentsView, so a Department Finance
   * Manager could open Roles & Access by typing the URL.
   */
  if (/\/departments\/[^/]+\/(roles|settings)(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.DepartmentsEdit };
  }
  /*
   * A department's own member list is department-owned data, not the branch member directory. The service
   * agrees: listMembers requires department DEPARTMENTS view and add/update/remove require department
   * DEPARTMENTS edit, and the branch Members module is never consulted. Falling through to the generic
   * `/members` prefix asked for branch-wide MEMBERS_VIEW instead, so the UI hid a page the API would serve —
   * a Department Manager could not open the member surface ADR-0027 puts inside their bundle.
   */
  if (/\/departments\/[^/]+\/members(?:\/|$)/.test(path)) {
    return { kind: "ACTION", action: TenantAuthorizationAction.DepartmentsView };
  }
  const match = ACTION_PATH_PREFIXES.find(([prefix]) =>
    path === prefix || path.endsWith(prefix) || path.includes(`${prefix}/`),
  );
  return match ? { kind: "ACTION", action: match[1] } : { kind: "DENY_PENDING_PRODUCT_POLICY" };
}
